/**
 * Client-side thread matching (aligned with Chat message:list filters and legacy main chat).
 */

import { getThreadDeviceIdCandidates } from './clearMainChatThreadUnreadInStore'

/**
 * @param {{ senderDeviceId?: string, senderUserId?: string, receiverDeviceId?: string, receiverUserId?: string }} message
 * @param {string} deviceId
 * @param {string} operatorUserId
 */
export function messageMatchesDeviceThread (message, deviceId, operatorUserId, devicesById = null) {
  if (!message || !deviceId) return false

  const candidates = devicesById
    ? getThreadDeviceIdCandidates(devicesById, deviceId)
    : [String(deviceId)]

  const receiverDeviceId = message.receiverDeviceId
  const senderDeviceId = message.senderDeviceId
  const senderUserId = message.senderUserId
  const receiverUserId = message.receiverUserId

  for (const id of candidates) {
    if (receiverDeviceId === id) return true
    if (senderUserId === operatorUserId && receiverDeviceId === id) return true
    if (senderDeviceId === id && receiverUserId === operatorUserId) return true
    if (senderDeviceId === id && receiverDeviceId === id) return true
  }

  return false
}

/**
 * @param {{ senderUserId?: string, receiverUserId?: string, senderDeviceId?: string }} message
 * @param {string} userId
 * @param {string} operatorUserId
 */
export function messageMatchesUserThread (message, userId, operatorUserId) {
  if (!message || !userId) return false
  if (message.senderDeviceId) return false

  return (
    (message.senderUserId === userId && message.receiverUserId === operatorUserId) ||
    (message.senderUserId === operatorUserId && message.receiverUserId === userId)
  )
}

/**
 * @param {object} message
 * @param {{ chatGroupId?: string | null, userId?: string | null, deviceId?: string | null }} context
 * @param {string | undefined} operatorUserId
 */
export function messageMatchesActiveChatContext (message, context, operatorUserId, devicesById = null) {
  if (!message) return false
  if (context?.chatGroupId) {
    const openGroupId = String(context.chatGroupId)
    const messageGroupId = String(
      message.receiverChatGroupId || message.chatGroupId || ''
    )
    return messageGroupId === openGroupId
  }
  if (context?.deviceId && operatorUserId) {
    return messageMatchesDeviceThread(message, context.deviceId, operatorUserId, devicesById)
  }
  if (context?.userId && operatorUserId) {
    return messageMatchesUserThread(message, context.userId, operatorUserId)
  }
  // Inbox / broadcast list — no thread selected; do not hide badge for every unread.
  return false
}

/**
 * Whether a socket/optimistic message should be prepended to the open chat message list.
 * Inbox mode (subscribed, no thread) shows all main-chat rows; thread mode uses context matching.
 *
 * @param {object} message
 * @param {{ chatGroupId?: string | null, userId?: string | null, deviceId?: string | null, subscribed?: boolean }} context
 * @param {string | undefined} operatorUserId
 * @param {Record<string, object> | null} [devicesById]
 */
export function messageShouldUpdateOpenChatList (message, context, operatorUserId, devicesById = null) {
  if (!message || !context?.subscribed) return false
  if (context.chatGroupId || context.deviceId || context.userId) {
    return messageMatchesActiveChatContext(message, context, operatorUserId, devicesById)
  }
  return !message.receiverChatGroupId
}
